'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useSocket } from '@/components/chat/hooks/useSocket'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { TrainingGeneratedEvent } from '@/server/data/cx-training'
import { hasWatcher } from './ai-generation-bus'

/**
 * App-wide listener for detached AI-generation results. Mounted globally in the
 * locale layout (mirrors GlobalChatNotifications) so the "Training fertig" toast
 * fires no matter where the user navigated after kicking off a generation.
 * The socket room (`group-${agencyId}`, joined server-side) already scopes the
 * event to the current agency, so no client-side filtering is needed.
 */
function Listener() {
  const t = useTranslations('TrainingBuilder')
  const { currentUser } = useCurrentUser()
  const { socket } = useSocket(currentUser?.token || null)

  useEffect(() => {
    if (!socket) return
    const onTraining = (payload: TrainingGeneratedEvent) => {
      // A dialog is open and showing the result in-place — don't double-notify.
      if (hasWatcher()) return
      if (payload.status === 'done') {
        const { chapters, lessons, questions } = payload.inserted
        toast.success(t('aiToastDone'), {
          description: t('aiDoneSummary', { chapters, lessons, questions }),
        })
      } else {
        toast.error(t('aiToastFailed'), { description: payload.message })
      }
    }
    socket.on('ai:training-generated', onTraining)
    return () => {
      socket.off('ai:training-generated', onTraining)
    }
  }, [socket, t])

  return null
}

export function AiGenerationListener() {
  // Client-only mount to avoid hydration issues (same pattern as GlobalChatNotifications).
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return null
  return <Listener />
}
